import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";

const EMPTY = { name: "", breed: "", age: "", color: "", owner_name: "", owner_email: "", owner_phone: "", notes: "" };

export default function HorseForm() {
  const { t } = useApp();
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (editing) api.get(`/horses/${id}`).then((r) => setForm({ ...EMPTY, ...r.data }));
  }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, breed: form.breed, age: form.age, color: form.color,
      owner_name: form.owner_name, owner_email: form.owner_email,
      owner_phone: form.owner_phone, notes: form.notes,
    };
    try {
      if (editing) await api.put(`/horses/${id}`, payload);
      else await api.post("/horses", payload);
      toast.success(t("t_saved"));
      nav("/admin");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || t("t_error"));
    }
  };

  const remove = async () => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/horses/${id}`);
    toast.success(t("t_deleted"));
    nav("/admin");
  };

  const field = (key, testid) => (
    <div>
      <Label className="mb-1.5 block text-sm">{t(key)}</Label>
      <Input data-testid={testid} value={form[key]} onChange={set(key)} required={key === "name"} className="rounded-[12px] focus-visible:ring-primary" />
    </div>
  );

  return (
    <div className="min-h-screen">
      <TopBar showBack backTo="/admin" showLogout />
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <h1 className="font-serif-h text-3xl text-foreground" data-testid="horse-form-title">
          {editing ? t("horse_form_edit") : t("horse_form_new")}
        </h1>
        <form onSubmit={save} className="mt-6 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {field("name", "hf-name")}
            {field("breed", "hf-breed")}
            {field("age", "hf-age")}
            {field("color", "hf-color")}
            {field("owner_name", "hf-owner-name")}
            {field("owner_email", "hf-owner-email")}
            {field("owner_phone", "hf-owner-phone")}
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">{t("notes")}</Label>
            <Textarea data-testid="hf-notes" value={form.notes} onChange={set("notes")} rows={4} className="rounded-[12px] focus-visible:ring-primary" />
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <Button type="submit" data-testid="hf-save" className="rounded-full bg-primary px-8 text-primary-foreground hover:bg-[#2C4633]">
              {t("save")}
            </Button>
            {editing && (
              <Button type="button" variant="outline" data-testid="hf-delete" onClick={remove} className="rounded-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground">
                <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} />{t("delete")}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
